import { ApolloError } from '@apollo/client';
import { PropsWithChildren } from 'react';
import { SpaceProps } from 'styled-system';
import { HelpDialog } from '~/components/dialogs';
import { LOGIN_URL } from '~/utils/constants';
import { errors } from '~/utils/error-messages';
import { isStorybook } from '~/utils/storybook';
import { TRACK, trackError, trackEvent } from '~/utils/tracking';
import { Alert, HelpDialogLink } from './error-message.style';

const report = (title: string, str?: string) =>
  trackError(new Error(title), { extras: { message: str || title } });

const SupportLink = ({ children }: PropsWithChildren<{}>) => (
  <HelpDialog target={<HelpDialogLink openInNewTab={false}>{children}</HelpDialogLink>} />
);

export const parse = (e: string | Error | ApolloError) => {
  let str;
  if (typeof e === 'string') str = e;
  else if (e.message) str = e.message;
  else str = '';

  if (str.includes('Session invalid or expired') || str.includes('Missing authorization')) {
    return (
      <span>
        Oops, your session expired. Please <a href={LOGIN_URL}>sign in</a>.
      </span>
    );
  }

  if (str.includes('Failed to fetch')) {
    report(str);
    return 'We seem to be having trouble connecting, please try again later.';
  }

  if (str.startsWith('Account:')) {
    report(str);
    return 'We had trouble creating your account. Please try again later.';
  }

  if (str.startsWith('Payment:')) {
    const allowable = [
      'An error occurred while processing your card. Try again in a little bit',
      'Bank account already exists in our records',
      'Credit card already exists in our records',
      "Sorry, we don't accept Discover cards",
      "Sorry, we don't accept prepaid cards",
      'Sorry, we only accept payment from credit cards issued within the United States',
      'Too many requests, please try again in a few minutes',
      'Your card does not support this type of purchase',
      'Your card has expired',
      'Your card has insufficient funds',
      'Your card number is incorrect',
      'Your card was declined',
      "Your card's security code is incorrect",
      "Your card's security code is invalid",
    ];

    if (allowable.find(allowed => str.includes(allowed))) {
      return [
        'Registering payment Failed:',
        'Response not successful:',
        'Unexpected error occurred:',
        'Validation failed:',
        'Payment: Error:',
      ]
        .reduce((message, prefix) => message.replace(prefix, ''), str)
        .trim();
    }

    if (str.includes('invalid CVC')) {
      return "Your card's CVC is invalid";
    }

    if (str.includes('There are no valid checking or savings')) {
      return 'We were unable to find any valid checking or savings account(s)';
    }

    if (str.includes('Routing number must have 9 digits')) {
      return 'Routing number must have 9 digits';
    }

    if (str.includes('Invalid amount')) {
      return 'Payment method authorization declined. Contact your bank for more information.';
    }

    if (str === errors.missingFetchStripeToken) {
      return (
        <span>
          We had trouble loading the payment form. Try refreshing the page or{' '}
          <SupportLink>contacting support</SupportLink>.
        </span>
      );
    }

    report('Generic payment error message shown', str);
    return 'We had trouble adding this payment method.';
  }

  if (str.includes(errors.expiredGoogleSession)) {
    return 'Your session expired. Please try again.';
  }

  if (str.startsWith('Resuming:')) {
    report(str);
    return 'We had trouble finding your account.';
  }

  if (str.includes('Please') || str.includes('Incorrect credentials') || isStorybook()) {
    return str;
  }

  if (str.includes('Account number already exists')) {
    trackEvent(TRACK.utilityCredsDuplicateResulted);
    return 'That account number already exists in our system.';
  }

  if (str.includes('Account number is in use by another utility account')) {
    return 'That account number already exists in our system.';
  }

  if (str.includes('Email is already taken')) {
    trackEvent(TRACK.emailAlreadyExistsResulted);
    return 'That email already exists in our system.';
  }

  if (str.includes('Username is already associated')) {
    trackEvent(TRACK.utilityCredsDuplicateResulted);
    return 'That username already exists in our system.';
  }

  if (str.includes('Email has already been verified')) {
    return (
      <span>
        That email already has an account. Please <a href={LOGIN_URL}>sign in</a>.
      </span>
    );
  }

  report(`Generic error message shown, caused by ${str}`);
  return 'Something went wrong, please try again.';
};

interface ErrorMessageProps extends SpaceProps {
  allow?: boolean; // if you want to avoid the parse logic
  error?: string | Error | ApolloError;
  // styled-system margin prop collides with Shrike Alert margin
  margin?: string;
  [x: string]: any;
}

export const ErrorMessage = ({ allow, error, ...rest }: ErrorMessageProps) => {
  if (!error) return null;
  const message = allow ? error : parse(error);

  return (
    <Alert data-testid="error-message" {...rest}>
      {message}
    </Alert>
  );
};
